// "The winery you asked about is in the catalogue now." The edge answers what became of this
// device's submissions (GET /v1/submissions/mine); an imported one that names a producer becomes a
// notice the app shows until the user has seen it, and tells the edge so (POST .../mine/seen). It
// is polled, not pushed, so it works with push switched off or denied: a Web Push is only an
// earlier tap on the shoulder for the same fact. Nothing is read beyond the producer's name.
import { SubmissionNotice } from "@proef/contract";
import { useSyncExternalStore } from "react";
import { z } from "zod";
import { isEdgeEnabled } from "../backends";
import { invalidate } from "../dataCache";
import { call } from "../edgeApi";
import { catalogueWineId } from "../wineIdentity";

const PositiveInt = z.number().int().positive();

/** `GET /v1/submissions/mine`: the contract's `{ items }`; one entry the shape does not fit is skipped, not fatal. */
const MineResponse = z
  .object({ items: z.array(z.unknown()) })
  .transform(({ items }): SubmissionNotice[] =>
    items.flatMap((raw) => {
      const parsed = SubmissionNotice.safeParse(raw);
      return parsed.success ? [parsed.data] : [];
    }),
  );

export interface WineryNotice {
  /** Every submission of this device that this producer answers; all are marked seen together. */
  readonly submissionIds: readonly number[];
  readonly producer: { readonly id: number; readonly name: string };
  /** The catalogue wine one of them became (the edge moved this device's own wine to it), when one did. */
  readonly wineId?: number | undefined;
}

interface State {
  notices: readonly WineryNotice[];
  /** Seen on the device but not yet told to the edge (offline, or a failed request). */
  seenPending: readonly number[];
  /** Relinked submissions whose tastings and cellar were already read again from the edge. */
  synced: readonly number[];
}

const STORAGE_KEY = "proef.notices";
/** A poll on focus is cheap, but a phone refocuses many times an hour. */
const MIN_POLL_MS = 5 * 60_000;
const MAX_KEPT = 200;

const EMPTY: State = { notices: [], seenPending: [], synced: [] };

const Stored = z.object({
  notices: z.array(
    z.object({
      submissionIds: z.array(PositiveInt),
      producer: z.object({ id: PositiveInt, name: z.string().min(1) }),
      wineId: PositiveInt.optional(),
    }),
  ),
  seenPending: z.array(PositiveInt),
  synced: z.array(PositiveInt).default([]),
});

function read(): State {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return EMPTY;
    const parsed = Stored.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

let state: State = typeof window === "undefined" ? EMPTY : read();
const listeners = new Set<() => void>();

function commit(next: State): void {
  state = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked or full: the notices last this session.
  }
  listeners.forEach((l) => {
    l();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The winery notice to show now, if any; the oldest first, so none is skipped over. */
export function firstNotice(): WineryNotice | undefined {
  return state.notices[0];
}

export function useWineryNotice(): WineryNotice | undefined {
  return useSyncExternalStore(subscribe, firstNotice, () => undefined);
}

/** Where the notice's action leads: the catalogue wine the user's own wine became, else the winery's page, which lists the wines that came with it. */
export function noticePath(notice: WineryNotice): string {
  return notice.wineId === undefined
    ? `/winery/${String(notice.producer.id)}`
    : `/wine/${String(notice.wineId)}`;
}

/** A winery is news once, however many submissions of this device it answers. */
function withNotices(items: readonly SubmissionNotice[], current: State): readonly WineryNotice[] {
  const notices = new Map(current.notices.map((n) => [n.producer.id, n]));
  for (const item of items) {
    const producer = item.result?.producer;
    if (item.status !== "imported" || item.seen || producer === undefined) continue;
    if (current.seenPending.includes(item.id)) continue;
    if (item.kind === "barcode") continue;
    const known = notices.get(producer.id);
    const ids = new Set(known?.submissionIds ?? []);
    ids.add(item.id);
    const wineId =
      item.relinked && item.wineId !== undefined ? catalogueWineId(item.wineId) : undefined;
    const open = wineId ?? known?.wineId;
    notices.set(producer.id, {
      submissionIds: [...ids],
      producer,
      ...(open === undefined ? {} : { wineId: open }),
    });
  }
  return [...notices.values()];
}

/** A notice the edge already holds as seen (from another tab, or an earlier flush) is stale. */
function withoutSeen(
  notices: readonly WineryNotice[],
  items: readonly SubmissionNotice[],
): readonly WineryNotice[] {
  const seen = new Set(items.filter((i) => i.seen).map((i) => i.id));
  const known = new Set(items.map((i) => i.id));
  return notices.filter((n) => !n.submissionIds.every((id) => seen.has(id) || !known.has(id)));
}

/**
 * The edge has moved the tastings and cellar rows of a relinked wine to its catalogue wine, so what
 * this device cached under the old one is stale: read both lists again, once per submission.
 */
async function resyncRelinked(items: readonly SubmissionNotice[]): Promise<void> {
  const fresh = items.filter((i) => i.relinked && !state.synced.includes(i.id)).map((i) => i.id);
  if (fresh.length === 0) return;
  await Promise.all(
    ["user:tastings", "user:cellar", "user:recommendations", "user:wine:"].map((prefix) =>
      invalidate(prefix),
    ),
  );
  commit({ ...state, synced: [...state.synced, ...fresh].slice(-MAX_KEPT) });
}

async function flushSeen(): Promise<void> {
  const ids = state.seenPending;
  if (ids.length === 0) return;
  try {
    await call(z.unknown(), "/v1/submissions/mine/seen", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
    commit({ ...state, seenPending: state.seenPending.filter((id) => !ids.includes(id)) });
  } catch {
    // Offline or refused: the next poll tells it again.
  }
}

let polling: Promise<void> | undefined;
let lastPoll = 0;

async function run(): Promise<void> {
  await flushSeen();
  let items: SubmissionNotice[];
  try {
    items = await call(MineResponse, "/v1/submissions/mine");
  } catch {
    // Offline, edge down or switched off: the notices already kept stay, nothing new is learned,
    // and the next focus may ask again at once.
    lastPoll = 0;
    return;
  }
  await resyncRelinked(items);
  commit({ ...state, notices: withoutSeen(withNotices(items, state), items) });
}

/**
 * Asks the edge what became of this device's submissions: new notices appear, wines the edge moved
 * to the catalogue are read again from it. Never throws and never runs twice at once; without `force` it also
 * leaves a poll alone that came less than five minutes after the last.
 */
export function pollSubmissions(force = false): Promise<void> {
  if (!isEdgeEnabled() || (typeof navigator !== "undefined" && !navigator.onLine))
    return Promise.resolve();
  if (!force && Date.now() - lastPoll < MIN_POLL_MS) return Promise.resolve();
  polling ??= (async () => {
    lastPoll = Date.now();
    await run();
  })().finally(() => {
    polling = undefined;
  });
  return polling;
}

/** The user has seen this notice (acted on it or closed it); the edge is told as soon as it can be. */
export function dismissNotice(notice: WineryNotice): void {
  commit({
    ...state,
    notices: state.notices.filter((n) => n.producer.id !== notice.producer.id),
    seenPending: [...new Set([...state.seenPending, ...notice.submissionIds])].slice(-MAX_KEPT),
  });
  void flushSeen();
}

const onFocus = (): void => {
  void pollSubmissions();
};
const onVisible = (): void => {
  if (document.visibilityState === "visible") void pollSubmissions();
};

/** The device-side alias map the edge's own relink replaced; one pass drops what an earlier build left behind. */
function dropLegacyAliases(): void {
  try {
    window.localStorage.removeItem("proef.wineAliases");
  } catch {
    // Storage blocked: nothing was stored here either.
  }
}

/** Polls now and whenever the app regains focus or the device comes back online. */
export function startNotices(): () => void {
  dropLegacyAliases();
  void pollSubmissions(true);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onFocus);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("online", onFocus);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** Test seam: forget notices, the poll clock and anything stored. */
export function resetNotices(): void {
  state = EMPTY;
  lastPoll = 0;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  listeners.forEach((l) => {
    l();
  });
}
